import { selectDataRequestedAppointment } from 'connect/selectors';
import { CheckoutTimerStateHandler } from 'connect/state/handlers/checkout-timer';
import { QuestionCircleFilled, Tooltip } from 'opencare-crown';
import Analytics from 'analytics-opencare';
import { useAnalyticsService } from 'services/AnalyticsService';

import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCountdown } from 'utils/customHooks/useCountdown';

import { selectDataClinicIsInstantBook } from 'pages/profile/connect/selectors';

import { CheckoutTimerEndModal } from '../CheckoutTimerEndModal/CheckoutTimerEndModal';

import { CheckoutTimerText, CheckoutTimerWrapper } from './styled-components';

import {
  CHECKOUT_TIMER_STATUSES,
  DEFAULT_CHECKOUT_TIMER_DATA,
} from './constants';

const formatTime = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

const { ENDED, RUNNING, CLEARED } = CHECKOUT_TIMER_STATUSES;

const analytics = new Analytics();

export const CheckoutTimer = ({ handleClose }) => {
  const analyticsService = useAnalyticsService();
  const dispatch = useDispatch();

  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const checkoutTimerData = useSelector(CheckoutTimerStateHandler.selector);
  const clinicIsInstantBook = useSelector(selectDataClinicIsInstantBook);

  const showTimerEndModal = checkoutTimerData?.status === ENDED;
  const [resetTrigger, setResetTrigger] = useState(false);

  const handleTick = timeLeft => {
    dispatch(
      CheckoutTimerStateHandler.update({
        timeLeft,
        slotId: requestedAppointment.slotId,
        status: timeLeft > 0 ? RUNNING : ENDED,
      }),
    );
  };

  const [timeLeft] = useCountdown(
    checkoutTimerData?.timeLeft,
    resetTrigger,
    handleTick,
  );

  const resetTimer = (clear = false) => {
    setResetTrigger(true);
    dispatch(
      CheckoutTimerStateHandler.update({
        ...DEFAULT_CHECKOUT_TIMER_DATA,
        ...clear && { status: CLEARED },
        slotId: requestedAppointment.slotId,
      }),
    );
  };

  useEffect(() => {
    if (!checkoutTimerData) {
      dispatch(CheckoutTimerStateHandler.update(DEFAULT_CHECKOUT_TIMER_DATA));
    }
  }, []);
  useEffect(() => {
    if (
      ![requestedAppointment.slotId, undefined].includes(
        checkoutTimerData?.slotId,
      )
    ) {
      resetTimer();
    }
  }, [requestedAppointment.slotId, resetTimer]);

  const returnToPracticeProfile = source => {
    resetTimer(true);
    handleClose();
    analytics.trackClick({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-checkout-timer-end',
      name: 'return-to-practice-profile',
      section: source || 'unknown',
      ...analyticsService.analyticsData,
    });
  };

  const handleContinueWithRequest = () => {
    resetTimer(true);
    analytics.trackClick({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-checkout-timer-end',
      name: 'continue-with-request',
      ...analyticsService.analyticsData,
    });
  };

  const checkoutTimerText = clinicIsInstantBook
    ? `Holding your time slot for ${formatTime(timeLeft)}`
    : `Holding your request for ${formatTime(timeLeft)}`;

  const tooltipContent = clinicIsInstantBook
    ? 'We are holding your time slot for you. You have to complete the booking within the time limit. If you do not complete the booking within the time limit, the slot will be released for others.'
    : 'Practice has limited availability. Checkout promptly to secure your booking!';

  if (!checkoutTimerData?.status) return null;

  return (
    <CheckoutTimerWrapper>
      {showTimerEndModal 
        && (
          <CheckoutTimerEndModal
            clinicIsInstantBook={clinicIsInstantBook}
            handleClose={returnToPracticeProfile}
            handleContinue={handleContinueWithRequest}
            isOpen={showTimerEndModal}
          />
        )
      }

      <CheckoutTimerText>
        {checkoutTimerText}
        <Tooltip title={tooltipContent}>
          <QuestionCircleFilled />
        </Tooltip>
      </CheckoutTimerText>
    </CheckoutTimerWrapper>
  );
};

CheckoutTimer.propTypes = {
  handleClose: propTypes.func.isRequired,
};
